import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Activity, Brain, Heart, ChevronRight } from 'lucide-react';
import { clients } from '../../../data/clients';
import { ClientProfile } from './ClientProfile';

interface ClientListProps {
  searchQuery?: string;
  filter?: string;
}

export function ClientList({ searchQuery = '', filter = 'all' }: ClientListProps) {
  const [selectedClient, setSelectedClient] = useState<string | null>(null);

  const filteredClients = clients.filter(client => {
    const matchesSearch = client.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filter === 'all' || client.program === filter;
    return matchesSearch && matchesFilter;
  });

  const selectedClientData = clients.find(c => c.id === selectedClient);

  if (selectedClientData) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <ClientProfile 
          client={selectedClientData}
          onBack={() => setSelectedClient(null)}
        />
      </motion.div>
    );
  }

  return (
    <div className="space-y-4">
      {filteredClients.map((client, index) => (
        <motion.button
          key={client.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          onClick={() => setSelectedClient(client.id)}
          className="w-full theme-card p-4 relative overflow-hidden group hover:bg-primary/10 transition-colors"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-50" />
          <div className="relative">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <img
                  src={client.image}
                  alt={client.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div className="text-left">
                  <div className="font-medium">{client.name}</div>
                  <div className="text-sm text-muted-foreground">{client.program}</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="text-sm font-medium">{client.progress}%</div>
                  <div className="text-xs text-muted-foreground">{client.lastActive}</div>
                </div>
                <ChevronRight className="w-5 h-5 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 mt-3">
              <div className="flex items-center gap-1 text-xs">
                <Heart className="w-3 h-3 text-primary" />
                <span>Recovery {client.metrics.recovery}%</span>
              </div>
              <div className="flex items-center gap-1 text-xs">
                <Activity className="w-3 h-3 text-primary" />
                <span>Strain {client.metrics.strain}</span>
              </div>
              <div className="flex items-center gap-1 text-xs">
                <Brain className="w-3 h-3 text-primary" />
                <span>Sleep {client.metrics.sleep}%</span>
              </div>
            </div>
          </div>
        </motion.button>
      ))}
    </div>
  );
}